import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import { callApi, HttpMethod } from "src/services/apiService";
import { useAppSelector } from "src/app/hooks";
import { selectAuth } from "src/features/authSlice";
import { useDashboardMetrics } from "./hooks/useDashboardMetrics";
import { useCarrierTrend } from "./hooks/useCarrierTrend";
import { useAccountConfig } from "./hooks/useAccountConfig";
import MetricCard from "./components/MetricCard";
import CardLoading from "./components/CardLoading";
import CarrierTrendChart from "./components/CarrierTrendChart";
import ScoreDistributionChart from "./components/ScoreDistributionChart";
import FlaggedBreakdownChart from "./components/FlaggedBreakdownChart";
import { getAttLabel, getAttLogo } from "./utils/carrierDisplay";
import { TC } from "./utils/trustCallApi";
import { toLocalIsoDate } from "./utils/dateHelpers";

interface CallVolumePoint {
  date: string;
  totalCalls: number;
  totalSpam: number;
}

// Default carrier date range: last 30 days. Returns yyyy-MM-dd strings for <input type=date>.
const defaultCarrierRange = (): { start: string; end: string } => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return { start: toLocalIsoDate(start), end: toLocalIsoDate(end) };
};

const MAX_CARRIER_RANGE_DAYS = 180;

// Parse YYYY-MM-DD as local date (not UTC) to avoid timezone off-by-one.
// Rejects overflow dates (e.g. Feb 31) that JS Date would silently normalize.
const parseLocalDate = (s: string): Date | null => {
  const parts = s.split("-");
  if (parts.length !== 3) return null;
  const [y, m, day] = [Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])];
  const d = new Date(y, m, day);
  if (isNaN(d.getTime())) return null;
  // Verify the date wasn't normalized (e.g. month/day overflow)
  if (d.getFullYear() !== y || d.getMonth() !== m || d.getDate() !== day) return null;
  return d;
};

const Dashboard: React.FC = () => {
  const { isSysadmin, acctName } = useAppSelector(selectAuth);
  const config = useAccountConfig();
  const metrics = useDashboardMetrics();

  const isCanadian = config.data?.isCanadian ?? false;
  // Gate on the remediation plan cap (attr 104), as Monitor.cshtml's HasRemediationDefaultValue does —
  // not IsRemediationClient (attr 96), which accounts with a remediation plan may lack.
  const isRemediation = config.data?.hasRemediationDefault ?? false;
  const hasAnalyticsAccess = config.data?.hasAnalyticsAccess ?? false;

  // Chart expand modal state
  const [expandedChart, setExpandedChart] = useState<string | null>(null);

  // Carrier-trend date range (DNC-294)
  const [carrierRange, setCarrierRange] = useState(defaultCarrierRange);
  const carrierStart = parseLocalDate(carrierRange.start);
  const carrierEnd = parseLocalDate(carrierRange.end);
  const carrierRangeValid = !!carrierStart && !!carrierEnd;
  const carrierRangeDays = carrierRangeValid
    ? Math.round((carrierEnd.getTime() - carrierStart.getTime()) / (1000 * 60 * 60 * 24))
    : NaN;
  const carrierRangeError = !carrierRangeValid
    ? "Please enter valid start and end dates."
    : carrierRangeDays < 0
      ? "Start date must be before end date."
      : carrierRangeDays > MAX_CARRIER_RANGE_DAYS
        ? "Date range cannot exceed 180 days."
        : undefined;

  const defaultRange = defaultCarrierRange();
  const carrierRangeDirty = carrierRange.start !== defaultRange.start || carrierRange.end !== defaultRange.end;

  const carrierTrendQuery = useCarrierTrend(carrierRange.start, carrierRange.end, !carrierRangeError);

  // Call volume chart (sysadmin + analytics accounts only)
  const callVolumeQuery = useQuery<CallVolumePoint[]>({
    queryKey: ["trustcall", "dashboard", "call-volume"],
    queryFn: () => callApi(TC.DASHBOARD_CALL_VOLUME, HttpMethod.GET),
    enabled: hasAnalyticsAccess,
  });

  // Get flagged count from flaggedTypeSummary — same data source as MVC's loadBarChart()
  const getFlaggedCount = (...categories: string[]): number | undefined => {
    if (!metrics.flaggedTypeSummary) return undefined;
    const item = metrics.flaggedTypeSummary.find((s) =>
      categories.includes(s.category)
    );
    return item?.value;
  };

  // Transform call volume dates to "Mon DD" format matching MVC, compute label step
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const callVolumeChartData = (callVolumeQuery.data ?? []).map((d) => {
    const parts = d.date.split("-");
    const label = MONTHS[parseInt(parts[1], 10) - 1] + " " + parseInt(parts[2], 10);
    return { ...d, label };
  });
  const callVolumeLabelStep = Math.max(1, Math.floor(callVolumeChartData.length / 15));

  // Nice-number algorithm for Y-axis ticks matching Kendo auto-scaling
  const getNiceTicks = (data: number[]): { ticks: number[]; max: number } => {
    const maxVal = Math.max(...data, 0);
    if (maxVal <= 0) return { ticks: [0], max: 0 };
    const roughStep = maxVal / 5;
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const residual = roughStep / magnitude;
    let step: number;
    if (residual <= 1.5) step = magnitude;
    else if (residual <= 3) step = 2 * magnitude;
    else if (residual <= 7) step = 5 * magnitude;
    else step = 10 * magnitude;
    const niceMax = Math.ceil(maxVal / step) * step;
    const ticks = Array.from({ length: niceMax / step + 1 }, (_, i) => i * step);
    return { ticks, max: niceMax };
  };
  const callsTicks = getNiceTicks(callVolumeChartData.map((d) => d.totalCalls));
  const spamTicks = getNiceTicks(callVolumeChartData.map((d) => d.totalSpam));

  // Find flags removed per carrier
  const getFlagsRemoved = (carrier: string): number | undefined => {
    if (!isRemediation || !metrics.flagsRemoved) return undefined;
    return metrics.flagsRemoved.find(
      (f) => f.carrier.toLowerCase() === carrier.toLowerCase()
    )?.removeFlagsCount;
  };

  return (
    <div>
      {/* Plan Status Cards — matching MVC: row > col-md-12 > card */}
      {metrics.serviceTypeSummary && (
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <h5 className="card-header" style={{ backgroundColor: "var(--navbar-and-treeview-bg)", color: "var(--navbar-link-color-fg)" }}><span className="panel-title">Account Name: {acctName || "N/A"}</span></h5>
              <div className="card-body d-flex gap-3">
            {/* Numbers Scanned Daily (Not Remediated) — green button matching MVC */}
            <button type="button" className="btn" style={{ cursor: "auto", backgroundColor: "var(--status-positive-bg)", border: "none", color: "var(--status-text)" }}>
              <span>Numbers Scanned Daily (Not Remediated)</span>&nbsp;
              <span className="badge" style={{ backgroundColor: "var(--navbar-and-treeview-bg)", color: "var(--navbar-link-color-fg)" }}>
                {metrics.serviceTypeSummary.monitoredCount} of {metrics.serviceTypeSummary.monitoredPlanLimit}
              </span>
            </button>

            {/* Numbers Under Remediation — yellow button matching MVC */}
            {(metrics.serviceTypeSummary.remediationPlanLimit > 0 ||
              metrics.serviceTypeSummary.remediationDailyScanPlanLimit > 0) && (
              <button type="button" className="btn" style={{ cursor: "auto", backgroundColor: "var(--status-warning-bg)", border: "none", color: "var(--status-text)" }}>
                <span>Numbers Under Remediation</span>&nbsp;
                <span className="badge" style={{ backgroundColor: "var(--navbar-and-treeview-bg)", color: "var(--navbar-link-color-fg)" }}>
                  {metrics.serviceTypeSummary.remediationCount + metrics.serviceTypeSummary.remediationDailyScanCount} of {metrics.serviceTypeSummary.remediationPlanLimit}
                </span>
                <div className="d-flex align-items-center justify-content-center gap-3 mt-1">
                  <span className="fw-bold">Scanning</span>
                  <span>Twice Weekly <span className="badge" style={{ backgroundColor: "var(--emphasis-txt-color)", color: "var(--card-bg)" }}>{metrics.serviceTypeSummary.remediationCount}</span></span>
                  <span>Daily <span className="badge" style={{ backgroundColor: "var(--emphasis-txt-color)", color: "var(--card-bg)" }}>
                    {metrics.serviceTypeSummary.remediationDailyScanCount} of {metrics.serviceTypeSummary.remediationDailyScanPlanLimit}
                  </span></span>
                </div>
              </button>
            )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Metric Cards Row — 6 cards matching MVC layout: col-xl-2 col-md-3 col-sm-4 col-xs-6 */}
      <div className="row">
        <MetricCard
          title="Clean"
          count={metrics.cleanCount}
          flagsRemoved={getFlagsRemoved("Clean")}
          isLoading={metrics.isLoading}
          label="NUMBERS"
          showFlagsRemoved={isRemediation}
        />
        <MetricCard
          title={getAttLabel(isCanadian)}
          count={getFlaggedCount("ATT", "Rogers/Bell")}
          flagsRemoved={getFlagsRemoved("ATT")}
          logoSrc={getAttLogo(isCanadian, "dark")}
          isLoading={metrics.isLoading}
          showFlagsRemoved={isRemediation}
        />
        <MetricCard
          title="Verizon"
          count={getFlaggedCount("Verizon")}
          flagsRemoved={getFlagsRemoved("Verizon")}
          logoSrc="/images/carriers_logo/verizon.png"
          isLoading={metrics.isLoading}
          showFlagsRemoved={isRemediation}
        />
        <MetricCard
          title="T-Mobile"
          count={getFlaggedCount("TMobile")}
          flagsRemoved={getFlagsRemoved("TMobile")}
          logoSrc="/images/carriers_logo/TMobile.png"
          isLoading={metrics.isLoading}
          showFlagsRemoved={isRemediation}
        />
        <MetricCard
          title="Apps"
          count={getFlaggedCount("AppStatus")}
          isLoading={metrics.isLoading}
          showHeightPlaceholder
        />
        <MetricCard
          title="FTC"
          count={metrics.ftcCount}
          isLoading={metrics.isLoading}
          showHeightPlaceholder
        />
      </div>

      {/* All charts in one row — matching MVC: row display-chart with margin-top:10px */}
      <div className="row" style={{ marginTop: 10 }}>
        {/* Flagged by Source */}
        <div className="col-xl-6 col-md-12 mt-3 mb-3">
          <FlaggedBreakdownChart
            data={metrics.flaggedTypeSummary}
            isLoading={metrics.isLoading}
            isCanadian={isCanadian}
            onExpand={() => setExpandedChart("flaggedSource")}
          />
        </div>

        {/* Flagged by carriers — date range picker (DNC-294) */}
        <div className="col-xl-6 col-md-12 mt-3 mb-3">
          <CarrierTrendChart
            data={carrierTrendQuery.data}
            isLoading={carrierTrendQuery.isLoading}
            onExpand={() => setExpandedChart("carrierTrend")}
            startDate={carrierRange.start}
            endDate={carrierRange.end}
            onStartDateChange={(v) => setCarrierRange((r) => ({ ...r, start: v }))}
            onEndDateChange={(v) => setCarrierRange((r) => ({ ...r, end: v }))}
            rangeError={carrierRangeError}
            onReset={carrierRangeDirty ? () => setCarrierRange(defaultCarrierRange()) : undefined}
          />
        </div>

        {/* Account Verizon Call Volume & Spam Reports (sysadmin + analytics access) — matches MVC */}
        {hasAnalyticsAccess && (
          <div className="col-12" style={{ marginTop: 15, marginBottom: 15 }}>
            <div className="card">
              <div className="card-header" style={{ backgroundColor: "var(--navbar-and-treeview-bg)", color: "var(--navbar-link-color-fg)" }}>
                <span className="panel-title">Account Verizon Call Volume &amp; Spam Reports</span>
                {isSysadmin && <span className="badge bg-secondary" style={{ fontSize: 9, verticalAlign: "middle", marginLeft: 4 }}>ADMIN</span>}
              </div>
              <div className="card-body">
                {callVolumeQuery.isLoading ? (
                  <CardLoading />
                ) : callVolumeQuery.data && callVolumeQuery.data.length > 0 ? (
                  <div className="row">
                    <div className="col-md-6">
                      {/* Chart title matching MVC Kendo title: { text: '...', font: '14px sans-serif' } */}
                      <div className="text-center fw-semibold" style={{ fontSize: "0.875rem" }}>
                        Total Verizon Calls (Last {callVolumeChartData.length} Days)
                      </div>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={callVolumeChartData}>
                          <XAxis
                            dataKey="label"
                            tick={{ fontSize: 10 }}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                            interval={callVolumeLabelStep - 1}
                          />
                          <YAxis tick={{ fontSize: 10 }} ticks={callsTicks.ticks} domain={[0, callsTicks.max]} label={{ value: "Calls", angle: -90, position: "insideLeft", style: { fontSize: 11 } }} />
                          <Tooltip formatter={(v: number) => [`${v.toLocaleString()} calls`]} labelStyle={{ color: "#333" }} itemStyle={{ color: "#333" }} />
                          <Bar dataKey="totalCalls" fill="var(--primary-btn-color)" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="col-md-6">
                      <div className="text-center fw-semibold" style={{ fontSize: "0.875rem" }}>
                        Total Verizon Spam Reports (Last {callVolumeChartData.length} Days)
                      </div>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={callVolumeChartData}>
                          <XAxis
                            dataKey="label"
                            tick={{ fontSize: 10 }}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                            interval={callVolumeLabelStep - 1}
                          />
                          <YAxis tick={{ fontSize: 10 }} ticks={spamTicks.ticks} domain={[0, spamTicks.max]} label={{ value: "Reports", angle: -90, position: "insideLeft", style: { fontSize: 11 } }} />
                          <Tooltip formatter={(v: number) => [`${v.toLocaleString()} report(s)`]} labelStyle={{ color: "#333" }} itemStyle={{ color: "#333" }} />
                          <Bar dataKey="totalSpam" fill="#FF4444" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="alert alert-info mb-0">No Verizon call volume data available.</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Spam Score Summary */}
        <div className="col-xl-6 col-md-12 mt-3 mb-3">
          <ScoreDistributionChart
            data={metrics.scoreSummary}
            isLoading={metrics.isLoading}
            title="Spam Score Summary"
            onExpand={() => setExpandedChart("spamScore")}
          />
        </div>

        {/* Max Spam Score Summary */}
        <div className="col-xl-6 col-md-12 mt-3 mb-3">
          <ScoreDistributionChart
            data={metrics.maxScoreSummary}
            isLoading={metrics.isLoading}
            title="Max Spam Score Summary"
            onExpand={() => setExpandedChart("maxScore")}
          />
        </div>
      </div>

      {/* Chart Expand Modal */}
      {expandedChart && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable tc-chart-expand">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {expandedChart === "flaggedSource" && "Flagged by Source"}
                  {expandedChart === "carrierTrend" && `Flagged by carriers: ${carrierRange.start} to ${carrierRange.end}`}
                  {expandedChart === "spamScore" && "Spam Score Summary"}
                  {expandedChart === "maxScore" && "Max Spam Score Summary"}
                </h5>
                <button type="button" className="btn-close" onClick={() => setExpandedChart(null)} />
              </div>
              <div className="modal-body">
                {expandedChart === "flaggedSource" && (
                  <FlaggedBreakdownChart data={metrics.flaggedTypeSummary} isLoading={false} isCanadian={isCanadian} />
                )}
                {expandedChart === "carrierTrend" && (
                  <CarrierTrendChart
                    data={carrierTrendQuery.data}
                    isLoading={carrierTrendQuery.isLoading}
                    startDate={carrierRange.start}
                    endDate={carrierRange.end}
                    onStartDateChange={(v) => setCarrierRange((r) => ({ ...r, start: v }))}
                    onEndDateChange={(v) => setCarrierRange((r) => ({ ...r, end: v }))}
                    rangeError={carrierRangeError}
                    onReset={carrierRangeDirty ? () => setCarrierRange(defaultCarrierRange()) : undefined}
                  />
                )}
                {expandedChart === "spamScore" && (
                  <ScoreDistributionChart data={metrics.scoreSummary} isLoading={false} title="Spam Score Summary" />
                )}
                {expandedChart === "maxScore" && (
                  <ScoreDistributionChart data={metrics.maxScoreSummary} isLoading={false} title="Max Spam Score Summary" />
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setExpandedChart(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
